import { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Copy, Check, ExternalLink, Clock, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const useCountdown = (iso) => {
  const [left, setLeft] = useState(() => (iso ? new Date(iso) - Date.now() : 0));
  useEffect(() => {
    if (!iso) return undefined;
    const t = setInterval(() => setLeft(new Date(iso) - Date.now()), 1000);
    return () => clearInterval(t);
  }, [iso]);
  if (left <= 0) return 'Expired';
  const h = Math.floor(left / 3.6e6), m = Math.floor((left % 3.6e6) / 6e4), s = Math.floor((left % 6e4) / 1000);
  return `${h}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`;
};

export const VenmoPaymentStep = ({ reservation, onUpdate }) => {
  const [copied, setCopied] = useState(false);
  const [reporting, setReporting] = useState(false);
  const countdown = useCountdown(reservation.status === 'pending' ? reservation.expires_at : null);
  const statusUrl = `${window.location.origin}/reservation/${reservation.code}`;

  const copy = async () => {
    try { await navigator.clipboard.writeText(reservation.code); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch { toast.error('Copy failed — long-press to select'); }
  };

  const reportSent = async () => {
    setReporting(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/reserve/${reservation.code}/payment-sent`);
      onUpdate?.(res.data);
      toast.success("Got it — we'll confirm as soon as it lands");
    } catch { toast.error('Could not update — try again'); } finally { setReporting(false); }
  };

  const StatusBanner = () => {
    if (reservation.status === 'confirmed') return (
      <div className="flex items-center gap-3 rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-emerald-800" data-testid="reservation-status-confirmed">
        <CheckCircle2 className="w-5 h-5 shrink-0" /><p className="text-sm font-semibold">Payment received — your bed is locked in. See you in 2027.</p>
      </div>);
    if (reservation.status === 'expired') return (
      <div className="flex items-center gap-3 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-red-800" data-testid="reservation-status-expired">
        <AlertTriangle className="w-5 h-5 shrink-0" /><p className="text-sm font-semibold">This hold expired. If you already paid, email us — otherwise start a new reservation.</p>
      </div>);
    if (reservation.status === 'cancelled') return (
      <div className="flex items-center gap-3 rounded-xl bg-gray-100 border border-gray-200 px-4 py-3 text-gray-700" data-testid="reservation-status-cancelled">
        <AlertTriangle className="w-5 h-5 shrink-0" /><p className="text-sm font-semibold">This reservation was cancelled.</p>
      </div>);
    return (
      <div className="flex items-center justify-between gap-3 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-amber-900" data-testid="reservation-status-pending">
        <div className="flex items-center gap-3"><Clock className="w-5 h-5 shrink-0" /><p className="text-sm font-semibold">{reservation.payment_reported_at ? 'Payment reported — awaiting confirmation' : 'Bed on hold — awaiting your Venmo payment'}</p></div>
        <span className="font-mono text-sm font-bold tabular-nums" data-testid="hold-countdown">{countdown}</span>
      </div>);
  };

  const pending = reservation.status === 'pending';

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden" data-testid="venmo-payment-step">
      <div className="bg-[#1a0d10] text-white px-6 md:px-10 py-6">
        <p className="eyebrow text-[10px] text-[#d9a15b] tracking-[0.3em] mb-1">{pending ? 'Step 3 of 3 · Pay with Venmo' : 'Your reservation'}</p>
        <h3 className="font-display text-2xl md:text-3xl font-semibold">{reservation.first_name}, your {reservation.tier_name} bed</h3>
        <p className="text-sm text-white/60 mt-1">Reference <span className="font-mono text-white/90">{reservation.code}</span> · ${reservation.price}</p>
      </div>

      <div className="p-6 md:p-10 space-y-8">
        <StatusBanner />

        {pending && (
          <div className="grid md:grid-cols-[auto,1fr] gap-8 items-start">
            <div className="mx-auto md:mx-0">
              <div className="rounded-2xl border-4 border-[#008cff]/20 bg-white p-4 shadow-lg inline-block" data-testid="venmo-qr">
                <QRCodeSVG value={reservation.pay_link} size={200} level="M" fgColor="#1a0d10" />
              </div>
              <p className="text-center text-[11px] text-gray-400 mt-2">Scan with your phone camera</p>
            </div>
            <div className="space-y-5">
              <div>
                <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase mb-2">Send exactly</p>
                <p className="font-display text-5xl font-medium tracking-tighter text-gray-900" data-testid="pay-amount">${reservation.price}</p>
              </div>
              <div>
                <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase mb-2">Put this code in the Venmo note</p>
                <div className="flex items-stretch gap-2">
                  <code className="flex-1 rounded-xl bg-wine-50 border border-wine/20 px-4 py-3 font-mono text-lg md:text-xl font-bold tracking-[0.2em] text-wine-800" data-testid="reservation-code">{reservation.code}</code>
                  <button type="button" onClick={copy} className="rounded-xl border border-gray-200 px-4 hover:bg-gray-50 transition-colors" data-testid="copy-code-btn" aria-label="Copy code">
                    {copied ? <Check className="w-5 h-5 text-emerald-600" /> : <Copy className="w-5 h-5 text-gray-600" />}
                  </button>
                </div>
              </div>
              <a href={reservation.pay_link} target="_blank" rel="noopener noreferrer" data-testid="open-venmo-btn"
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#008cff] hover:bg-[#0077d9] text-white font-semibold py-4 text-base transition-colors shadow-lg shadow-[#008cff]/25">
                Open Venmo{reservation.venmo_username ? ` · @${reservation.venmo_username}` : ''} <ExternalLink className="w-4 h-4" />
              </a>
              <ol className="text-sm text-gray-600 space-y-1.5 list-decimal pl-5">
                <li>Open Venmo (button above or scan the QR).</li>
                <li>Send <strong>${reservation.price}</strong> — the amount must match.</li>
                <li>Paste <strong>{reservation.code}</strong> in the payment note.</li>
                <li>Tap the button below so we know to look for it.</li>
              </ol>
              {!reservation.payment_reported_at ? (
                <button type="button" onClick={reportSent} disabled={reporting} data-testid="payment-sent-btn"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full border-2 border-wine text-wine hover:bg-wine hover:text-white font-semibold py-3.5 text-sm transition-colors disabled:opacity-50">
                  {reporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} I've sent my payment
                </button>
              ) : (
                <p className="text-sm text-emerald-700 font-medium inline-flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Thanks — we'll confirm by email once it lands.</p>
              )}
            </div>
          </div>
        )}

        <div className="rounded-xl bg-gray-50 border border-gray-200 px-4 py-3 text-xs text-gray-500">
          Bookmark your reservation page: <a href={statusUrl} className="text-wine font-medium underline break-all" data-testid="status-link">{statusUrl}</a>. We also emailed it to {reservation.email}.
        </div>
      </div>
    </div>
  );
};
